import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AffiliateNetworkService } from "@gen/tracker/v1/affiliate_network_connect";
import type { AffiliateNetwork } from "@gen/tracker/v1/affiliate_network_pb";
import type { Campaign, CampaignListItem } from "@gen/tracker/v1/campaign_pb";

import { createClient } from "@/lib/connect";
import { addDays, dayIn } from "@/lib/period";
import { useAllAffiliateNetworks } from "@/lib/services/network-service";
import { useOffersByIds } from "@/lib/services/offer-service";
import { useListPostbacks } from "@/lib/services/postback-service";
import { useReportTimezone } from "@/lib/services/workspace-service";

/**
 * The partner networks a campaign's leads go to: the networks of the offers
 * it routes to, read by id (not looked for among the first page of the
 * catalog, where most twins' offers are not).
 */
export function useCampaignNetworks(campaign: Campaign, item: CampaignListItem | undefined): { networks: AffiliateNetwork[]; loading: boolean } {
  const offers = useOffersByIds(item?.offers.map((o) => o.id) ?? campaign.offerWeights.map((o) => o.offerId));
  const all = useAllAffiliateNetworks();
  const networks = useMemo(() => {
    const ids = new Set([...(offers.data?.values() ?? [])].map((o) => o.networkId));
    return (all.data?.items ?? []).filter((n) => ids.has(n.id));
  }, [offers.data, all.data]);
  return { networks, loading: offers.isLoading || all.isLoading };
}

/** A network's newest postback of the last week — «последний настоящий — 40 с назад». */
export function useLastPostback(networkId: string, campaignId?: string) {
  const tz = useReportTimezone();
  const to = dayIn(new Date(), tz);
  return useListPostbacks({ from: addDays(to, -7), to, timezone: tz, networkId, campaignId, limit: 1, offset: 0 });
}

const networkClient = createClient(AffiliateNetworkService);

/**
 * How a partner's status words read today (ListNetworkStatuses): the words
 * it sent in the last 30 days or its mapping names, each with our status —
 * the card's «Статус партнёрки → У нас».
 */
export function useNetworkStatuses(networkId: string) {
  return useQuery({
    queryKey: ["network-statuses", networkId] as const,
    enabled: !!networkId,
    staleTime: 60_000,
    queryFn: async () => (await networkClient.listNetworkStatuses({ networkId })).items,
  });
}
